import { useParams } from 'react-router-dom'
import { useContext, useState, useEffect } from 'react'
import { APIContext } from 'context/APIContext'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import { FilterWorkoutByWorkoutLength } from 'functions/workoutUtils'
import moment from 'moment'

import ScreenFrame from 'components/Frame/ScreenFrame'
import ContentFrame from 'components/Frame/ContentFrame'
import WorkoutV5Set from 'components/Workout/WorkoutV5Set'

export default function WorkoutV5SummaryView() {

    const { urlWorkoutId } = useParams()

    const { GetWorkoutV5 } = useContext(APIContext)

    const { exerciseHashMap, exercises } = useGetExercises()

    const [isLoading, setIsLoading] = useState(true)
    const [workout, setWorkout] = useState(null)
    const [expandPhaseJSON, setExpandPhaseJSON] = useState({})

    const LoadWorkout = async () => {

        setIsLoading(true)

        const loadedWorkout = await GetWorkoutV5(urlWorkoutId)

        // A workout trained at a selected length leaves out the slots outside it (mobility workouts have no selected length)
        const selectedWorkoutLength = loadedWorkout?.selectedWorkoutLength ?? null
        const newWorkout = loadedWorkout == null || selectedWorkoutLength === null ? loadedWorkout : FilterWorkoutByWorkoutLength(loadedWorkout, selectedWorkoutLength)

        // All phases start expanded

        var defaultExpandPhaseJSON = {}
        newWorkout?.phases?.forEach((phase) => { defaultExpandPhaseJSON[phase.workoutPhaseId] = true })

        setExpandPhaseJSON(defaultExpandPhaseJSON)
        setWorkout(newWorkout)
        setIsLoading(false)

    }

    const TogglePhaseExpand = (workoutPhaseId) => {
        setExpandPhaseJSON((currentExpandPhaseJSON) => ({
            ...currentExpandPhaseJSON,
            [workoutPhaseId]: !currentExpandPhaseJSON[workoutPhaseId]
        }))
    }

    useEffect(() => { LoadWorkout() }, [urlWorkoutId])

    if (
        isLoading
        || Object.keys(exerciseHashMap).length === 0
        || exercises.length === 0
    ) {
        return (
            <ScreenFrame>
                <div className='max-w-2xl mx-auto space-y-4'>
                    <div className='flex-shrink-0 h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='flex-shrink-0 h-[400px] bg-gray-200 rounded-lg animate-pulse' />
                </div>
            </ScreenFrame>
        )
    }

    if (workout === null) {
        return (
            <ScreenFrame>
                <div className='max-w-2xl mx-auto space-y-4'>
                    <h1 className="text-2xl font-semibold text-gray-900 tracking-tight pt-2">
                        Workout Summary
                    </h1>

                    <ContentFrame>
                        <div className="p-10 space-y-2 text-center">
                            <p className="text-gray-600">There was an error loading this workout.</p>
                            <p className="text-sm text-gray-500">If issues persist please contact our support team at support@kinesisintegrated.com</p>
                        </div>
                    </ContentFrame>
                </div>
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame>
            <div className='max-w-2xl mx-auto space-y-4'>
                <ContentFrame>
                    <div className="w-full p-4 pt-8 space-y-4">
                        <div className="text-center space-y-1">
                            <div className="text-xl font-semibold leading-6 text-gray-900">
                                {workout.name}
                            </div>

                            {workout.date && (
                                <p className="text-sm text-gray-500">
                                    {moment(workout.date, 'YYYY-MM-DD').format('dddd, MMMM Do YYYY')}
                                </p>
                            )}
                        </div>

                        {workout.phases.map((phase) => {
                            const isExpanded = expandPhaseJSON[phase.workoutPhaseId] === true

                            return (
                                <div
                                    key={phase.workoutPhaseId}
                                    className="rounded-xl overflow-hidden text-left"
                                >
                                    <div
                                        className={`
                                            p-4 flex items-center bg-gray-100 cursor-pointer border-b
                                            transition-colors duration-200 ease-out
                                            ${isExpanded ? 'border-gray-300' : 'border-transparent'}
                                        `}
                                        onClick={() => TogglePhaseExpand(phase.workoutPhaseId)}
                                    >
                                        <p className="flex-grow">{phase.name}</p>

                                        <ChevronDownIcon
                                            className={`
                                                h-5 w-5 shrink-0
                                                transition-transform duration-200 ease-out
                                                ${isExpanded ? 'rotate-180' : 'rotate-0'}
                                            `}
                                        />
                                    </div>

                                    <div
                                        className={`
                                            grid transition-[grid-template-rows] duration-300 ease-out
                                            ${isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}
                                        `}
                                    >
                                        <div className="overflow-hidden min-h-0">
                                            <div
                                                className={`
                                                    divide-y divide-gray-300
                                                    transition-opacity duration-200 ease-out
                                                    ${isExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                                                `}
                                            >
                                                {phase.type === 'circuit' && (
                                                    <>
                                                        {Array.from({ length: phase.sets }).map((_, setIndex) => (
                                                            <div
                                                                key={setIndex}
                                                                className='divide-y divide-gray-300'
                                                            >
                                                                {phase.slots.map((workoutSlot) => (
                                                                    <WorkoutV5Set
                                                                        key={workoutSlot.sets[setIndex]?.workoutSetId}
                                                                        exerciseHashMap={exerciseHashMap}
                                                                        setIndex={setIndex}
                                                                        exerciseId={workoutSlot.exerciseId}
                                                                        setsAssigned={phase.sets}
                                                                        workoutSet={workoutSlot.sets[setIndex]}
                                                                    />
                                                                ))}
                                                            </div>
                                                        ))}
                                                    </>
                                                )}

                                                {phase.type === 'straight' && (
                                                    <>
                                                        {phase.slots.map((workoutSlot) => (
                                                            <div key={workoutSlot.workoutSlotId}>
                                                                {workoutSlot.sets.map((workoutSet, setIndex) => (
                                                                    <WorkoutV5Set
                                                                        key={workoutSet.workoutSetId}
                                                                        exerciseHashMap={exerciseHashMap}
                                                                        setIndex={setIndex}
                                                                        exerciseId={workoutSlot.exerciseId}
                                                                        setsAssigned={workoutSlot.sets.length}
                                                                        workoutSet={workoutSet}
                                                                    />
                                                                ))}
                                                            </div>
                                                        ))}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </ContentFrame>
            </div>
        </ScreenFrame>
    )
}
